import { ADD_STEP_SUGGESTED_MAX, isFacingTrigger, STEP_CATEGORY, WORKFLOW_FIRST_PATH, type WorkflowStepType, type WorkflowTrigger } from "@sagot/constants";
import { isTicketTrigger } from "../../service/service-facts";
import type { Step } from "../../types";
import type { AddStepEntry } from "./add-step-catalog";

/** Triggers that open the chat before the visitor has said anything: Path A starts with content. */
const CONTENT_FIRST_TRIGGERS: ReadonlySet<string> = new Set(["widget_opened", "home_option", "visitor_visits_page", "visitor_clicks_element"]);
/** Steps worth having once in a path; they are not suggested again after they are there. */
const ONCE_STEP_TYPES: ReadonlySet<string> = new Set(["expected_reply_time", "ask_rating", "verify_email", "add_summary_note"]);

type Choices = readonly string[];

const AFTER_WAITING = (facing: boolean): Choices => [facing ? "send_message" : "notify_staff", "close_conversation"];

/** What usually follows a step (ids of steps or presets). A step that does not exist yet is skipped when the list is built. */
const AFTER_LAST: Readonly<Record<string, Choices | ((facing: boolean) => Choices)>> = {
  send_message: ["reply_buttons", "collect_reply", "collect_data", "let_assistant_answer"],
  collect_data: ["branch", "set_attribute", "send_message", "http_request"],
  collect_reply: ["branch", "set_attribute", "send_message", "http_request"],
  http_request: ["branch", "send_message", "set_attribute"],
  ask_rating: ["branch", "add_note", "close_conversation"],
  wait: AFTER_WAITING,
  snooze: AFTER_WAITING,
  let_assistant_answer: ["hand_to_person", "ask_rating", "end"],
  disable_reply: ["reply_buttons", "ask_rating", "collect_data"],
  enable_reply: ["let_assistant_answer", "collect_reply", "send_message"],
  set_language: ["send_message", "let_assistant_answer", "reply_buttons"],
  show_article: ["reply_buttons", "ask_rating", "let_assistant_answer", "hand_to_person"],
  verify_email: ["send_message", "branch", "hand_to_person", "http_request"],
  // Service desk (M27)
  ticket_intake: ["send_message", "hand_to_person", "end"],
  show_ticket_form: ["send_message", "end"],
  show_my_tickets: ["reply_buttons", "let_assistant_answer", "hand_to_person"],
  create_ticket: ["link_ticket", "notify_contact", "add_ticket_note", "assign_ticket"],
};
/** What follows a question whose answer is the visitor's e-mail: the code that proves it. */
const AFTER_EMAIL: Choices = ["verify_email", "branch", "set_attribute", "send_message"];
/** After an action: a related action, then the common ones. */
const AFTER_ACTION: Readonly<Record<string, Choices>> = {
  assign: ["add_note"],
  add_tags: ["set_priority"],
  add_summary_note: ["assign", "notify_staff"],
  route_ticket: ["notify_team", "notify_contact", "add_ticket_note"],
  assign_ticket: ["notify_team", "notify_contact", "add_ticket_note"],
  notify_contact: ["add_ticket_note", "set_ticket_state", "request_csat"],
  set_ticket_state: ["notify_contact", "add_ticket_note", "request_csat"],
  update_ticket: ["notify_contact", "add_ticket_note"],
};

const COMMON_ACTIONS: Choices = ["add_note", "add_tags", "assign", "set_attribute"];

interface SuggestionContext {
  trigger: WorkflowTrigger;
  pathId: string;
  steps: readonly Step[];
}

function openingChoices({ trigger, pathId }: SuggestionContext): Choices {
  if (pathId === WORKFLOW_FIRST_PATH && CONTENT_FIRST_TRIGGERS.has(trigger)) return ["send_message", "reply_buttons", "collect_data", "let_assistant_answer"];
  if (isFacingTrigger(trigger)) return ["send_message", "reply_buttons", "let_assistant_answer", "hand_to_person"];
  if (isTicketTrigger(trigger)) return ["notify_team", "notify_contact", "assign_ticket", "update_ticket"];
  return ["branch", "add_tags", "assign", "add_note"];
}

/** A question that saves the visitor's e-mail (`collect_data` or `collect_reply` to the visitor's e-mail field). */
const asksForEmail = (step: Step): boolean => (step.type === "collect_data" || step.type === "collect_reply") && step.save_to === "visitor.email";

function choicesAfter(last: Step, { trigger, steps }: SuggestionContext): Choices {
  if (asksForEmail(last) && isFacingTrigger(trigger)) return AFTER_EMAIL;
  const known = AFTER_LAST[last.type];
  if (known) return typeof known === "function" ? known(isFacingTrigger(trigger)) : known;
  if (STEP_CATEGORY[last.type] !== "actions") return [];
  const present = new Set<string>(steps.map((step) => step.type));
  return [...(AFTER_ACTION[last.type] ?? []), ...COMMON_ACTIONS].filter((id) => !present.has(id));
}

/**
 * Up to four next steps for the end of a path, from a fixed table (no model call). The first matching row wins; a step the
 * trigger or plan locks, one that does not exist, and a "once" step already in the path are left out.
 */
export function suggestedEntries(context: SuggestionContext, catalog: readonly AddStepEntry[]): AddStepEntry[] {
  const last = context.steps.at(-1);
  const wanted = last ? choicesAfter(last, context) : openingChoices(context);
  const present = new Set<WorkflowStepType>(context.steps.map((step) => step.type));
  const picked: AddStepEntry[] = [];
  for (const id of wanted) {
    const entry = catalog.find((candidate) => candidate.id === id);
    if (!entry || entry.locked || picked.includes(entry)) continue;
    if (ONCE_STEP_TYPES.has(entry.type) && present.has(entry.type)) continue;
    picked.push(entry);
  }
  return picked.slice(0, ADD_STEP_SUGGESTED_MAX);
}
